'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

interface FaqItem {
  question: string;
  answer: string;
}

interface FaqCategory {
  id: string;
  label: string;
  description: string;
  items: FaqItem[];
}

const faqCategories: FaqCategory[] = [
  {
    id: 'ai-translation',
    label: 'AI Translation',
    description: 'How AI and LLMs are transforming enterprise translation',
    items: [
      {
        question: 'What is AI translation and how does it differ from traditional machine translation?',
        answer: 'AI translation uses large language models (LLMs) and neural machine translation (NMT) engines powered by deep learning transformer architectures to convert text between languages with contextual understanding of tone and nuance, a significant advance over older rule-based and statistical MT systems. Unlike earlier systems, modern AI engines (GPT-4, Claude, Gemini, DeepL) generate fluent, contextually appropriate translations that can rival human translators for many content types. At Into23, we use the term "AI Translation with Verification" to describe our approach: leveraging the speed and scale of AI while ensuring every output passes through certified human review for guaranteed accuracy.',
      },
      {
        question: 'What is the difference between NMT (Neural Machine Translation) and LLM-based translation?',
        answer: 'Neural Machine Translation (NMT) engines like Google Translate and DeepL are purpose-built systems trained specifically on parallel bilingual corpora, meaning millions of aligned sentence pairs. They excel at producing fluent translations quickly and are optimized for throughput. LLM-based translation uses general-purpose large language models (GPT-4, Claude, Gemini) that were trained on vast amounts of multilingual text. LLMs bring advantages in handling context, ambiguity, creative content, and following specific instructions. The optimal approach, which Into23 employs through Into23 Workspace, is to select the best-fit engine per language pair and content domain, combining the reliability of NMT with the contextual intelligence of LLMs.',
      },
      {
        question: 'What languages does AI translation support effectively?',
        answer: 'Into23 supports AI translation across 75+ languages, with quality varying by language pair: high-resource pairs (English to/from Spanish, French, German, Chinese, Japanese, Korean) produce consistently strong results, while medium-resource languages (Hindi, Arabic, Thai, Vietnamese) have improved dramatically with LLMs but may need more human oversight. Low-resource languages (many African, Southeast Asian, and indigenous languages) remain challenging for AI. Our Into23 Workspace platform provides access to 17+ MT/LLM engines, allowing us to select the optimal engine for each specific language pair and content type.',
      },
      {
        question: 'Will AI replace human translators?',
        answer: 'AI is transforming the role of human translators rather than replacing them. The industry is shifting toward "human-in-the-loop AI" where technology handles scale and speed while humans provide judgment, cultural intelligence, and quality assurance. For high-volume, lower-risk content (internal communications, knowledge base articles), AI can handle 80-90% of the work with light human review. For high-stakes content (legal, medical, marketing campaigns), human expertise remains essential, though AI dramatically accelerates the process. At Into23, we invest in both our AI platform (Into23 Workspace) and our global network of certified linguists because both are essential to the future of language operations.',
      },
    ],
  },
  {
    id: 'traditional-localization',
    label: 'Traditional Localization',
    description: 'Translation Memory, fuzzy matching, and CAT tools',
    items: [
      {
        question: 'What is Translation Memory (TM) and how does it work?',
        answer: 'Translation Memory (TM) is a database that stores previously translated text segments as source-target pairs, automatically reusing them when identical or similar content appears in future projects, reducing costs, ensuring consistency, and accelerating turnaround. When new content is submitted, the TM compares each segment against stored translations: a "100% match" (exact match) is applied automatically, while "fuzzy matches" show similar segments with a percentage similarity score. Into23 maintains client-specific TMs that integrate with both our human workflows and our AI translation pipeline through Into23 Workspace, meaning your TM investment compounds in value over time.',
      },
      {
        question: 'What is the difference between translation, localization, and transcreation?',
        answer: 'Translation converts text between languages preserving original meaning; localization adapts content for a specific market including cultural references, date/number formats, currency, and layout direction; transcreation recreates the message in the target language to achieve the same emotional impact, commonly required for marketing slogans and advertising campaigns. Into23 offers all three levels of service. Our AI translation pipeline handles translation and many localization tasks efficiently, while transcreation projects are led by specialist creative linguists with AI-assisted ideation support.',
      },
      {
        question: 'What file formats can be processed for translation?',
        answer: 'Modern translation workflows support an extensive range of file formats. Common document formats include Microsoft Word (.docx), Excel (.xlsx), PowerPoint (.pptx), PDF, and InDesign (.idml). For software and web content, we handle JSON, XML, XLIFF, PO/POT, YAML, HTML, and Markdown. Multimedia workflows support SRT/VTT subtitle files, and we can extract translatable content from design tools like Figma and Sketch. Into23\'s Into23 Workspace platform supports 50+ file formats for direct upload, and our API accepts structured content in JSON and XLIFF for automated pipeline integration.',
      },
    ],
  },
  {
    id: 'mt-llm-engines',
    label: 'MT & LLM Engines',
    description: 'Machine translation engines, LLMs, and engine selection',
    items: [
      {
        question: 'What MT and LLM engines does Into23 support?',
        answer: 'Into23\'s Into23 Workspace platform provides unified access to 17+ machine translation and LLM engines through a single interface, including Google Cloud Translation, DeepL, Microsoft Translator, Amazon Translate, OpenAI GPT-4/GPT-4o, Anthropic Claude, Google Gemini, and leading Chinese AI models (Baidu Ernie, Alibaba Qwen, Moonshot Kimi, MiniMax). Each engine has different strengths: DeepL excels in European languages, Google handles the broadest language coverage, GPT-4 is strong for creative and contextual content, and specialized Chinese models outperform Western LLMs for CJK language pairs.',
      },
      {
        question: 'What is best-fit engine selection and why does it matter?',
        answer: 'Best-fit engine selection is Into23\'s approach to choosing the optimal translation engine for each specific project based on language pair, content type, domain, and quality requirements. No single engine is best for everything. DeepL may outperform GPT-4 for German legal text, while GPT-4 may produce better results for creative Japanese marketing copy. Our Into23 Workspace platform maintains performance benchmarks across all supported engines, updated continuously based on LQA scores from verified projects. Clients can also override the recommendation and select a preferred engine manually.',
      },
      {
        question: 'Can I train or fine-tune a custom MT engine for my content?',
        answer: 'Yes. Through Into23\'s partnership with Custom.MT, clients can train custom MT engines using their own bilingual data (minimum 50,000+ segment pairs required), producing output that matches their specific terminology, style, and domain vocabulary with less post-editing needed. Custom-trained engines are particularly valuable for clients with large, recurring translation volumes in specialized domains such as pharmaceutical companies, legal firms, or technology companies with proprietary terminology. Into23 manages this entire process as part of our Enterprise AI Ops tier.',
      },
    ],
  },
  {
    id: 'quality-assurance',
    label: 'Quality Assurance',
    description: 'LQA scoring, MTPE, and Into23 Verify+ workflows',
    items: [
      {
        question: 'What is LQA (Linguistic Quality Assurance) and how is it measured?',
        answer: 'Linguistic Quality Assurance (LQA) is a systematic evaluation of translation quality using the MQM (Multidimensional Quality Metrics) framework, which categorizes errors by type (accuracy, fluency, terminology, style) and severity (critical, major, minor), producing a weighted quality score on a 0-100 scale, with scores above 95 considered publication-ready. Into23 generates LQA reports for every verified translation project, including error categorization, severity assessment, reviewer identification, and an overall quality score. These reports provide the objective quality data that enterprise clients need for vendor management and compliance documentation.',
      },
      {
        question: 'What is MTPE (Machine Translation Post-Editing)?',
        answer: 'Machine Translation Post-Editing (MTPE) is the process where a human linguist reviews and corrects machine-translated output, typically 30-60% faster than translating from scratch and proportionally less expensive. There are two standard levels: Light Post-Editing (LPE) produces comprehensible, accurate output suitable for internal communications, while Full Post-Editing (FPE) produces publication-quality output indistinguishable from human translation, required for client-facing and regulated content.',
      },
      {
        question: 'How does Into23\'s Into23 Verify+ process work?',
        answer: 'Our verification process follows a structured workflow: First, content is translated by the selected AI engine through the Into23 Workspace platform. The AI output is then routed to a certified linguist who specializes in the relevant language pair and domain. The linguist reviews every segment for accuracy, fluency, terminology consistency, cultural appropriateness, and adherence to client style guides. After verification, an automated QA check runs for technical issues. Finally, an LQA report is generated with the quality score, error breakdown, reviewer ID, and complete edit history.',
      },
    ],
  },
  {
    id: 'data-security',
    label: 'Data Security & Privacy',
    description: 'Confidentiality, GDPR, data residency, and compliance',
    items: [
      {
        question: 'How does Into23 protect confidential content during translation?',
        answer: 'Into23 protects confidential content through end-to-end encryption (TLS 1.3 in transit, AES-256 at rest), Alibaba Cloud infrastructure with APAC data residency options, strict need-to-know access controls with full audit logging, and NDAs with all linguists and staff. Our MT engine partner Custom.MT holds ISO 27001 certification, and Into23 holds ISO 9001 and ISO 17100 certifications, with our own ISO 27001 certification currently in progress.',
      },
      {
        question: 'Does my content get used to train AI models?',
        answer: 'No. Into23 works exclusively with enterprise-tier API services from MT and LLM providers (including OpenAI, Google, DeepL, and Anthropic) that are designed not to use API inputs for model training. When we send content through our Into23 Workspace platform, we use enterprise API tiers that are structured to ensure your data is processed and discarded, not retained for training purposes. This is a key distinction from consumer-grade tools like free Google Translate or ChatGPT, where user inputs may be used to improve models.',
      },
      {
        question: 'Is Into23 GDPR compliant?',
        answer: 'Yes. Into23 maintains full compliance with the General Data Protection Regulation (GDPR) for all content involving EU personal data. Our compliance measures include: lawful basis documentation for all processing activities; data minimization practices; right to erasure procedures; breach notification protocols (within 72 hours as required); and regular privacy impact assessments. For clients subject to other data protection regimes (PDPA in Singapore, PIPL in China, APPI in Japan), we adapt our practices accordingly.',
      },
    ],
  },
  {
    id: 'data-plus',
    label: 'Into23 Data+',
    description: 'Multilingual data for AI training, evaluation, and safety',
    items: [
      {
        question: 'What are AI Data Services and why do AI companies need them?',
        answer: 'AI Data Services are human-powered tasks required to train, evaluate, and improve AI systems, including LLM evaluation, RLHF annotation, multilingual AI safety testing, and creation of annotated training datasets. Into23\'s AI Data Services division specializes in multilingual scenarios: evaluating AI performance across languages, providing human feedback in non-English languages, and creating training data for multilingual AI systems. Our team of trained annotators across 6 priority languages (English, Chinese, Hindi, Japanese, Korean, Arabic) supports AI companies building global products.',
      },
      {
        question: 'What is Multilingual AI Safety Testing?',
        answer: 'Multilingual AI Safety Testing is the practice of probing AI systems for safety vulnerabilities using native-speaker adversarial testers across multiple languages — not just English. Most AI safety programs focus primarily on English, which leaves significant gaps: code-switching attacks (prompts that blend two or more languages) succeed 46.7% more often than English-only attacks, and low-resource languages are approximately three times more likely to generate harmful content under attack conditions. Into23 specialises in this multilingual dimension — native-speaker adversarial prompt creation, output rating, and cultural harm assessment across APAC languages including Bahasa Indonesia, Cantonese, Hindi, Japanese, Korean, Mandarin, Tagalog, Thai, and Vietnamese.',
      },
      {
        question: 'What is RLHF (Reinforcement Learning from Human Feedback)?',
        answer: 'RLHF (Reinforcement Learning from Human Feedback) is the training technique that aligns large language models with human preferences by having evaluators compare pairs of model outputs and indicate which is better, whether more helpful, accurate, appropriate, or safer, then using this preference data to train a reward model that guides the LLM toward preferred outputs. RLHF is the technique behind the dramatic improvement in models like ChatGPT, Claude, and Gemini. Into23 provides multilingual RLHF annotation services where trained evaluators assess model outputs across multiple languages.',
      },
    ],
  },
  {
    id: 'workspace-platform',
    label: 'Into23 Workspace',
    description: 'A secure workspace for managing multilingual operations',
    items: [
      {
        question: 'What is Into23 Workspace and how does it work?',
        answer: 'Into23 Workspace is Into23\'s cloud-based translation management portal that gives enterprise clients unified access to 17+ MT and LLM engines through a single interface. Upload files (50+ formats supported) or send content via REST API, select or auto-assign the optimal translation engine, and receive translated output with optional Into23 Verify+. The platform includes a real-time cost dashboard showing usage and spend across all engines, project management tools for tracking translation status, and automated LQA reporting. Access is via web portal at workspace.into23.com or through our API.',
      },
      {
        question: 'Can I integrate Into23 Workspace with my existing systems via API?',
        answer: 'Yes. Into23 Workspace provides a comprehensive REST API that enables automated translation workflows. Common integrations include CMS platforms (translating content automatically when new pages are published), product information management systems, customer support platforms, and CI/CD pipelines. The API supports both synchronous (real-time) and asynchronous (batch) translation requests, with webhook notifications for job completion. We provide API documentation, SDKs for popular programming languages, and integration support during onboarding.',
      },
      {
        question: 'Is Into23 Workspace available for beta access?',
        answer: 'Yes. Into23 Workspace is currently in beta with limited enterprise pilot slots available for Q1-Q2 2026, offering early access, dedicated onboarding support, preferential pricing, and direct input into feature development priorities. Beta access is well suited for enterprises currently managing multi-engine translation workflows manually or those looking to consolidate their translation technology stack. To request beta access, visit our Workspace page or contact us directly at hello@into23.com.',
      },
    ],
  },
  {
    id: 'enterprise-pricing',
    label: 'Enterprise & Pricing',
    description: 'Engagement models, pricing tiers, and onboarding',
    items: [
      {
        question: 'What are Into23\'s service tiers and pricing models?',
        answer: 'Into23 offers three service tiers: Tier 1 AI Basic ($0.03–$0.08/word) for internal documents and lower-risk content with AI-only translation; Tier 2 Into23 Verify+ ($0.08–$0.15/word) for client-facing and regulated content where every word is AI-translated then verified by a certified linguist; and Tier 3 Enterprise AI Ops (annual partnerships from $300K–$3M) for large-scale programs with dedicated teams, custom engine training, and SLA-backed delivery. All tiers include access to the Into23 Workspace platform, Translation Memory leverage discounts, and LQA reporting.',
      },
      {
        question: 'What industries does Into23 specialize in?',
        answer: 'Into23 serves enterprise clients across several key verticals. Legal: contracts, litigation support, regulatory filings, patent documentation. Life Sciences: clinical trial documentation, regulatory submissions, patient-facing materials, medical device labeling. eCommerce: product descriptions, marketing campaigns, customer support content. Consumer Electronics: user manuals, UI strings, marketing materials, support documentation. AI & Technology: training data, model evaluation, safety testing. Our APAC base gives us particular strength in CJK (Chinese, Japanese, Korean) language pairs.',
      },
      {
        question: 'Where is Into23 located and what markets do you serve?',
        answer: 'Into23 is headquartered in Hong Kong with offices in Beijing, Shanghai, Bangalore, and Tokyo, providing strong coverage across Asian languages, time zones, and markets while serving enterprise clients across North America, Europe, and Asia-Pacific. Our APAC footprint gives us direct access to key Asian markets and large pools of multilingual talent for both translation and AI data services. We have particular strength serving companies headquartered in NA/EU that are scaling operations in APAC markets.',
      },
    ],
  },
];

export default function FaqPage() {
  const [activeCategory, setActiveCategory] = useState('ai-translation');
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  const toggleItem = (key: string) => {
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const activeData = faqCategories.find(c => c.id === activeCategory);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* ===== HERO ===== */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-cobalt/5 via-background to-background" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-8">
          <span className="text-cobalt font-mono text-sm tracking-widest uppercase mb-4 block">
            Knowledge Base
          </span>
          <h1 className="font-display font-bold text-5xl lg:text-6xl text-silver-text leading-tight mb-6 max-w-3xl">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-silver-muted max-w-2xl leading-relaxed mb-6">
            Everything you need to know about AI translation, localization technology, data security, and working with Into23.
          </p>
          <div className="flex items-center gap-6 text-sm text-silver-muted">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-cobalt inline-block" />
              59 questions answered
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-cobalt inline-block" />
              8 categories
            </span>
          </div>
        </div>
      </section>

      {/* ===== FAQ CONTENT ===== */}
      <section className="py-12 lg:py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-10">

            {/* Sidebar category nav */}
            <aside className="lg:w-64 flex-shrink-0">
              <div className="lg:sticky lg:top-28 space-y-1">
                {faqCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setActiveCategory(cat.id);
                      setOpenItems({});
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                      activeCategory === cat.id
                        ? 'bg-cobalt/10 border border-cobalt/30 text-cobalt-glow' :'text-silver-muted hover:text-silver-text hover:bg-graphite'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </aside>

            {/* FAQ items */}
            <div className="flex-1 min-w-0">
              {activeData && (
                <>
                  <div className="mb-8">
                    <h2 className="font-display font-bold text-2xl text-silver-text mb-2">
                      {activeData.label}
                    </h2>
                    <p className="text-silver-muted text-sm">{activeData.description}</p>
                  </div>

                  <div className="space-y-3">
                    {activeData.items.map((item, idx) => {
                      const key = `${activeData.id}-${idx}`;
                      const isOpen = openItems[key];
                      return (
                        <div
                          key={key}
                          className={`glow-card rounded-xl overflow-hidden transition-all ${isOpen ? 'border-cobalt/30' : ''}`}
                        >
                          <button
                            onClick={() => toggleItem(key)}
                            className="w-full flex items-start justify-between gap-4 p-6 text-left"
                          >
                            <span className="font-medium text-silver-text leading-snug">
                              {item.question}
                            </span>
                            <span className={`flex-shrink-0 w-6 h-6 rounded-full border flex items-center justify-center transition-all mt-0.5 ${
                              isOpen ? 'border-cobalt bg-cobalt/10 text-cobalt-glow' : 'border-steel/40 text-silver-muted'
                            }`}>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                              >
                                <path d="m6 9 6 6 6-6" />
                              </svg>
                            </span>
                          </button>
                          {isOpen && (
                            <div className="px-6 pb-6 border-t border-steel/20">
                              <p className="text-silver-muted leading-relaxed text-sm pt-4">
                                {item.answer}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="glow-card rounded-2xl p-10 lg:p-16 text-center">
            <span className="text-cobalt font-mono text-sm tracking-widest uppercase mb-4 block">
              Ready to Get Started?
            </span>
            <h2 className="font-display font-bold text-3xl lg:text-4xl text-silver-text mb-4">
              Still have questions?
            </h2>
            <p className="text-silver-muted max-w-xl mx-auto mb-8 leading-relaxed">
              Whether you need AI translation at scale or multilingual AI data services, Into23 has the expertise and technology to deliver.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                href="/get-a-quote"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-cobalt text-white font-semibold rounded-lg hover:bg-cobalt/90 transition-colors"
              >
                Contact Sales
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </Link>
              <Link
                href="/resources"
                className="inline-flex items-center gap-2 px-8 py-3.5 border border-steel/30 text-silver-muted hover:text-silver-text hover:border-cobalt/30 font-medium rounded-lg transition-colors"
              >
                Explore Resources
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
